// src/components/EventForm.js
import React, { useState } from 'react';
import styled from 'styled-components';

const FormContainer = styled.div`
  width: 100%;
  max-width: 500px;
  padding: 30px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 40px;
  border: 1px solid rgba(179, 139, 109, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(to right, #d4b59e, #b38b6d);
    opacity: 0.8;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #5a4a42;
  font-size: 1.05rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0d5c5;
  border-radius: 4px;
  font-size: 16px;
  background-color: rgba(255, 255, 255, 0.8);
  color: #5a4a42;
  font-family: 'Cormorant Garamond', serif;
  transition: all 0.3s ease;
  box-sizing: border-box;
  max-width: 100%;
  
  &:focus {
    outline: none;
    border-color: #b38b6d;
    box-shadow: 0 0 0 3px rgba(179, 139, 109, 0.2);
    background-color: white;
  }
  
  &::placeholder {
    color: #c0b0a0;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  flex-shrink: 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(to right, #b38b6d, #d4b59e);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(179, 139, 109, 0.3);
  font-family: 'Cormorant Garamond', serif;
  letter-spacing: 1px;
  
  &:hover {
    background: linear-gradient(to right, #a07a5f, #c4a58e);
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(179, 139, 109, 0.4);
  }
`;

const StatusMessage = styled.div`
  margin-top: 20px;
  text-align: center;
  font-weight: bold;
  font-size: 1.1rem;
  color: ${props => props.success ? '#5a9367' : props.error ? '#c56b6b' : 'inherit'};
`;

const EventForm = () => {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zipcode: '',
    cannot_attend: false
  });
  
  const [statusMessage, setStatusMessage] = useState({
    text: '',
    type: '' // 'success' or 'error'
  });
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.firstname) {
      setStatusMessage({ text: 'Please enter your First Name.', type: 'error' });
      return;
    }
    if (!formData.lastname) {
      setStatusMessage({ text: 'Please enter your Last Name.', type: 'error' });
      return;
    }
    if (!formData.email) {
      setStatusMessage({ text: 'Please enter your email.', type: 'error' });
      return;
    }
    if (!formData.address_street) {
      setStatusMessage({ text: 'Please enter your mailing address.', type: 'error' });
      return;
    }
    if (!formData.address_city) {
      setStatusMessage({ text: 'Please enter your City.', type: 'error' });
      return;
    }
    if (!formData.address_state) {
      setStatusMessage({ text: 'Please enter your State.', type: 'error' });
      return;
    }
    if (!formData.address_zipcode) {
      setStatusMessage({ text: 'Please enter your zipcode.', type: 'error' });
      return;
    }
    
    try {
      // Prepare data for API
      const data = {
        id: formData.email.toLowerCase(),
        firstName: formData.firstname,
        lastName: formData.lastname,
        address_street: formData.address_street,
        address_city: formData.address_city,
        address_state: formData.address_state,
        address_zip: formData.address_zipcode,
        timestamp: new Date().toISOString(),
        status: 'Save The Date Received',
        rsvp: formData.cannot_attend ? 'cannot attend' : 'not yet',
        cannot_attend: formData.cannot_attend
      };
      
      // Make API request
      const response = await fetch('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          TableName: 'erikaandrew_wedding',
          Item: data
        })
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      // Reset form on success
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        address_street: '',
        address_city: '',
        address_state: '',
        address_zipcode: '',
        cannot_attend: false
      });
      
      setStatusMessage({
        text: `Thank you ${formData.firstname}! Your Information has been recorded.`,
        type: 'success'
      });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatusMessage({
        text: `${error}. There was an error submitting your information. Please try again.`,
        type: 'error'
      });
    }
  };
  
  return (
    <FormContainer>
      <form onSubmit={handleSubmit}>
        <InputGroup>
          <Label htmlFor="firstname">Please Enter Your First Name:</Label>
          <Input 
            type="text" 
            id="firstname" 
            name="firstname" 
            placeholder="First Name"
            value={formData.firstname}
            onChange={handleChange}
          />
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="lastname">Please Enter Your Last Name:</Label>
          <Input 
            type="text" 
            id="lastname" 
            name="lastname" 
            placeholder="Last Name"
            value={formData.lastname}
            onChange={handleChange}
          />
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="email">Please Enter Your Preferred Email:</Label>
          <Input 
            type="text" 
            id="email" 
            name="email" 
            placeholder="me@emailprovider.com"
            value={formData.email}
            onChange={handleChange}
          />
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="address_street">Please Enter Your Street Name and Number:</Label>
          <Input 
            type="text" 
            id="address_street" 
            name="address_street" 
            placeholder="123 Main St"
            value={formData.address_street}
            onChange={handleChange}
          />
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="address_city">Please Enter Your City:</Label>
          <Input 
            type="text" 
            id="address_city" 
            name="address_city" 
            placeholder="Irvine"
            value={formData.address_city}
            onChange={handleChange}
          />
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="address_state">Please Enter Your State:</Label>
          <Input 
            type="text" 
            id="address_state" 
            name="address_state" 
            placeholder="CA"
            value={formData.address_state}
            onChange={handleChange}
          />
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="address_zipcode">Please Enter Your Zipcode:</Label>
          <Input 
            type="text" 
            id="address_zipcode" 
            name="address_zipcode" 
            placeholder="90000"
            value={formData.address_zipcode}
            onChange={handleChange}
          />
        </InputGroup>
        
        <CheckboxGroup>
          <Checkbox 
            type="checkbox" 
            id="cannot_attend" 
            name="cannot_attend"
            checked={formData.cannot_attend}
            onChange={handleChange}
          />
          <Label htmlFor="cannot_attend">Please check this box if you CAN'T make it</Label>
        </CheckboxGroup>
        
        <SubmitButton type="submit">Submit</SubmitButton>
        
        {statusMessage.text && (
          <StatusMessage 
            success={statusMessage.type === 'success'} 
            error={statusMessage.type === 'error'}
          >
            {statusMessage.text}
          </StatusMessage>
        )}
      </form>
    </FormContainer>
  );
};

export default EventForm;